import { Select as BaseSelect } from "@base-ui/react/select";
import { Check, ChevronsUpDown } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import { cn } from "../utils";

export const Select = BaseSelect.Root;

export function SelectTrigger({
  className,
  placeholder,
  ...props
}: ComponentProps<typeof BaseSelect.Trigger> & { placeholder?: string }) {
  return (
    <BaseSelect.Trigger
      className={cn(
        "flex h-8 min-w-32 cursor-pointer items-center justify-between gap-2",
        "rounded-control border border-line-strong bg-surface-raised px-2.5",
        "text-[0.8125rem] text-ink shadow-raised",
        "transition-[border-color] duration-150 hover:border-ink-faint",
        "data-[popup-open]:border-ember",
        className,
      )}
      {...props}
    >
      <BaseSelect.Value
        className="truncate data-[placeholder]:text-ink-faint"
        placeholder={placeholder}
      />
      <BaseSelect.Icon>
        <ChevronsUpDown className="size-3.5 text-ink-muted" />
      </BaseSelect.Icon>
    </BaseSelect.Trigger>
  );
}

export function SelectContent({
  className,
  children,
  ...props
}: ComponentProps<typeof BaseSelect.Popup>) {
  return (
    <BaseSelect.Portal>
      <BaseSelect.Positioner
        sideOffset={4}
        // Keep the list anchored to the trigger. Aligning the selected item
        // with the trigger clips options above it and animates the panel as
        // it reflows — feels frozen or "items sliding in" on reopen.
        alignItemWithTrigger={false}
        className="z-50 outline-none"
      >
        <BaseSelect.Popup
          className={cn(
            "max-h-72 min-w-[var(--anchor-width)] overflow-y-auto rounded-surface border border-line bg-surface-overlay p-1 shadow-overlay",
            "origin-[var(--transform-origin)] transition-[transform,opacity] duration-150 ease-swift",
            "data-[starting-style]:scale-95 data-[starting-style]:opacity-0",
            "data-[ending-style]:scale-95 data-[ending-style]:opacity-0",
            className,
          )}
          {...props}
        >
          {children}
        </BaseSelect.Popup>
      </BaseSelect.Positioner>
    </BaseSelect.Portal>
  );
}

export function SelectItem({
  className,
  children,
  ...props
}: ComponentProps<typeof BaseSelect.Item> & { children: ReactNode }) {
  return (
    <BaseSelect.Item
      className={cn(
        "flex cursor-pointer items-center justify-between gap-2 rounded-control px-2 py-1.5",
        "text-[0.8125rem] text-ink-secondary outline-none",
        "transition-[background-color,color,transform] duration-100 ease-swift",
        "active:scale-[0.98]",
        "data-[highlighted]:bg-surface-sunken data-[highlighted]:text-ink",
        className,
      )}
      {...props}
    >
      <BaseSelect.ItemText>{children}</BaseSelect.ItemText>
      <BaseSelect.ItemIndicator>
        <Check className="size-3.5 text-ember" />
      </BaseSelect.ItemIndicator>
    </BaseSelect.Item>
  );
}
